import { CommonModule } from '@angular/common';
import {
	Component,
	QueryList,
	ViewChildren,
	Input,
	computed,
	inject,
	effect,
	signal,
	AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { ISessionEntry } from '../timing-tower/IRaceTiming';
import { VbLxpCheckboxComponent } from '../controls/checkbox/vb-lxp-checkbox.component';
import { VbLxpSwitchComponent } from '../controls/switch/vb-lxp-switch.component';
import { TimespanPipe } from '../shared/time-span.pipe';
import { TimingTowerComponent } from '../timing-tower/timing-tower.component';
import { VbLxpPanelComponent } from '../panel/vb-lxp-panel.component';
import { VbLxpVideoSlotComponent } from '../video/video-slot/video-slot.component';
import { WindowSizeService } from '../shared/window-size.service';
import { VideoLayout, VideoLayoutService } from '../shared/video-layout.service';
import { FeatureSettingsService } from '../shared/feature-settings.service';
import { PanelService } from '../panel/panel.service';
import { StreamData } from '../shared/stream.token';
import { RaceSessionService } from '../timing-tower/race-session.service';
import { RaceClassComponent } from '../shared/race-class.component';
import { ENABLE_TWITCH_STREAM_SELECTOR } from '../shared/livestream-options.token';

import { LivestreamViewService } from './livestream-view.service';
import { TwitchChatComponent } from '../twitch-chat/twitch-chat.component';
import { WebSocketService } from '../shared/websocket.service';

const LAYOUT_CLASS_MAP: Record<VideoLayout, string> = {
	[VideoLayout.SINGLE]: 'aspect-16/9',
	[VideoLayout.SIDE_BY_SIDE]: 'aspect-32/9',
	[VideoLayout.LARGE_LEFT]: 'aspect-48/18',
	[VideoLayout.LARGE_RIGHT]: 'aspect-48/18',
	[VideoLayout.GRID_2X2]: 'aspect-16/9',
	[VideoLayout.STACKED]: 'aspect-16/18',
};

@Component({
	selector: 'app-livestream-view',
	imports: [
		CommonModule,
		TimespanPipe,
		RouterModule,
		VbLxpCheckboxComponent,
		VbLxpPanelComponent,
		VbLxpSwitchComponent,
		VbLxpVideoSlotComponent,
		TwitchChatComponent,
		TimingTowerComponent,
		RaceClassComponent,
	],
	templateUrl: './livestream-view.component.html',
	styleUrl: './livestream-view.component.css',
	host: {
		'[style.height]': `'100%'`,
	},
})
export class LivestreamViewComponent implements AfterViewInit {
	@Input() public streamChatTitle = 'SRO Stream Chat';
	@Input() public enableStreamChatPopout = true;
	@Input() public streamChatPopoutUrl =
		`https://www.twitch.tv/popout/sromotorsports/chat?popout=&parent=${window.location.hostname}`;

	@ViewChildren('livestreamPlayerSlot')
	currentLivestreamPlayerSlots: QueryList<VbLxpVideoSlotComponent>;

	public layoutService = inject(VideoLayoutService);
	public raceSessionService = inject(RaceSessionService);

	private windowSizeService = inject(WindowSizeService);
	private panelService = inject(PanelService);
	private featureSettingsService = inject(FeatureSettingsService);
	public showTwitchStreamSelector =
		inject(ENABLE_TWITCH_STREAM_SELECTOR, { optional: true }) ?? false;
	public livestreamViewService = inject(LivestreamViewService);
	public webSocketService = inject(WebSocketService);

	public VideoLayout = VideoLayout;
	public isMobile = toSignal(this.windowSizeService.isMobile$);
	public moreColumns = signal(false);
	public isMainStreamSelected = signal(true);
	public isSessionArchived = signal(false);
	public prevIsSessionArchived = signal(false);
	public raceStatus = toSignal(this.raceSessionService.raceStatus$);
	public timeStatus = toSignal(this.raceSessionService.timeStatus$);
	public featureSettings = toSignal(this.featureSettingsService.featureSettings$);
	public selectedClasses = toSignal(this.raceSessionService.selectedClasses$, { initialValue: [] });

	public hidePlayerMobile = computed(() => {
		return this.isMobile() && this.livestreamViewService.isNoPlayerSelected();
	});

	protected containerAspectRatioClass = '';

	constructor() {
		this.layoutService.activeLayout$.subscribe((activeLayout: VideoLayout) => {
			this.containerAspectRatioClass = LAYOUT_CLASS_MAP[activeLayout];
			setTimeout(() => {
				const currentPlayers = this.currentLivestreamPlayerSlots
					.toArray()
					.map((lps) => lps.mvPlayer);
				this.livestreamViewService.updateCurrentPlayers(currentPlayers);
			});
		});

		this.raceSessionService.isSessionArchived$
			.pipe(takeUntilDestroyed())
			.subscribe((isArchived) => {
				this.prevIsSessionArchived.set(this.isSessionArchived());
				this.isSessionArchived.set(isArchived);
				if (isArchived && !this.layoutService.isLayout(VideoLayout.SINGLE)) {
					this.layoutService.setLayout(VideoLayout.SINGLE);
				}
				if (isArchived && this.panelService.isPanelOpen('timing')) {
					this.panelService.closePanel('timing');
				}
				// Open timing tower when session switches from archived to not archived
				if (this.prevIsSessionArchived() && !this.isSessionArchived()) {
					this.panelService.openPanel('timing');
				}
			});

		effect(() => {
			if (this.moreColumns()) {
				this.panelService.closePanel('chat');
			}
		});

		effect(() => {
			const chatOpen = this.chatPanelOpen();
			if (chatOpen && this.moreColumns()) {
				this.panelService.closePanel('timing');
				setTimeout(() => this.panelService.openPanel('chat'));
			}
		});

		effect(() => {
			const timingOpen = this.timingPanelOpen();
			if (timingOpen && this.moreColumns()) {
				this.panelService.closePanel('chat');
				setTimeout(() => this.panelService.openPanel('timing'));
			}
		});
	}

	private chatPanelOpen = toSignal(this.panelService.getPanelState('chat'));
	private timingPanelOpen = toSignal(this.panelService.getPanelState('timing'));

	ngAfterViewInit(): void {
		// SSE connection is now managed centrally in app.ts

		const currentPlayers = this.currentLivestreamPlayerSlots.toArray().map((lps) => lps.mvPlayer);
		this.livestreamViewService.updateCurrentPlayers(currentPlayers);
		if (this.isSessionArchived() && this.panelService.isPanelOpen('timing')) {
			this.panelService.closePanel('timing');
		}
	}

	public openFullTimingPage() {
		const currentQueryParams = window.location.search;
		const newRoute = '/full-timing';
		const fullUrl = `${newRoute}${currentQueryParams}`;

		window.open(fullUrl, '_blank');
	}

	public openFullMapPage() {
		const currentParams = new URLSearchParams(window.location.search).toString();
		const base = new URL('map-popout', document.baseURI).toString();
		const url = currentParams ? `${base}?${currentParams}` : base;
		const width = 1280;
		const height = 720;
		window.open(url, '_blank', `width=${width},height=${height},resizable=yes`);
	}

	public openStreamChatPopout() {
		if (!this.streamChatPopoutUrl) {
			return;
		}

		const width = 420;
		const height = 760;
		const left = Math.max((window.screen.width - width) / 2, 0);
		const top = Math.max((window.screen.height - height) / 2, 0);

		window.open(
			this.streamChatPopoutUrl,
			'stream-chat-popout',
			`popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
		);
	}

	public onTimingTowerSelectionAdded(team: ISessionEntry) {
		this.livestreamViewService.addSlotItem({
			type: 'timing',
			id: team.number,
			streamData: this.livestreamViewService.getStreamDataByCarNumber(team.number),
		});
	}

	public onTimingTowerSelectionRemoved(team: ISessionEntry) {
		this.livestreamViewService.removeSlotItem({
			type: 'timing',
			id: team.number,
			streamData: this.livestreamViewService.getStreamDataByCarNumber(team.number),
		});
	}

	public onTwitchStreamToggled(isSelected: boolean) {
		if (isSelected) {
			this.livestreamViewService.addSlotItem({ type: 'twitchStream' });
			this.livestreamViewService.isTwitchStreamSelected.set(true);
		} else {
			this.livestreamViewService.removeSlotItem({ type: 'twitchStream' });
			this.livestreamViewService.isTwitchStreamSelected.set(false);
		}
	}

	public onPlayerClosed(streamData: StreamData) {
		if (streamData.carNumber) {
			this.livestreamViewService.removeSlotItem({
				type: 'timing',
				id: streamData.carNumber,
				streamData,
			});
		} else if (streamData.id === 'main-stream') {
			this.livestreamViewService.removeSlotItem({
				type: 'mainStream',
			});
		} else if (streamData.id === 'twitch-stream') {
			this.livestreamViewService.removeSlotItem({
				type: 'twitchStream',
			});
		}
	}

	onClassSelectionChange(selectedClasses: string[]) {
		this.raceSessionService.updateSelectedClasses(selectedClasses);
	}
}
